import type { TrackerAction, TrackerTap } from '@awwyeah/shared';
import { memberTint } from '../../../theme/memberColor';
import type { RosterMember } from '../useHouseholdRoster';

/**
 * One tappable action — the whole interaction surface of a tracker.
 *
 * The visual grammar, and the reasoning behind it:
 *
 *  - **Freshness is opacity, never colour.** A tint in the last tapper's member colour
 *    sits behind the pill at `opacity: freshness`, so a just-walked dog glows in Dan's
 *    colour and fades to plain surface over the action's cadence. Nothing ever goes red,
 *    amber, bold or urgent — the module's contract (`@awwyeah/shared` trackers docblock)
 *    is that a stale tracker states a fact, it doesn't scold anyone.
 *  - **The caption is the point.** "2h · Faye" is the answer to "has anyone fed the dog",
 *    which is the entire reason this screen exists. The button is just how you add to it.
 *  - **Never tapped reads as "—"**, not "0 times" and not "overdue". There is no count in
 *    this module and nothing is ever due.
 */
export function ActionPill({
  action,
  lastTap,
  lastBy,
  freshness,
  agoLabel,
  onTap,
}: {
  action: TrackerAction;
  lastTap: TrackerTap | null;
  lastBy: RosterMember | null;
  /** `1` = just tapped, `0` = a full cadence ago (or never). Drives the tint's opacity. */
  freshness: number;
  agoLabel: string | null;
  onTap: () => void;
}): JSX.Element {
  const tint = lastBy ? memberTint(lastBy.color) : 'var(--app-raised)';

  return (
    <button
      type="button"
      onClick={onTap}
      data-testid="tracker-action-pill"
      data-action-id={action.id}
      data-freshness={freshness.toFixed(2)}
      aria-label={
        lastTap && agoLabel && lastBy
          ? `${action.label} — last done ${agoLabel} ago by ${lastBy.name}. Tap to log it again.`
          : `${action.label} — not done recently. Tap to log it.`
      }
      className="relative flex min-h-[84px] min-w-[76px] flex-1 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-md border border-line bg-surface px-2 py-2.5 transition-transform duration-150 ease-app active:scale-95"
    >
      {/* The fading tint. Purely decorative and inert — it must never intercept the tap. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 transition-opacity duration-500 ease-app"
        style={{ backgroundColor: tint, opacity: freshness }}
      />
      <span className="relative text-[22px] leading-none" aria-hidden="true">
        {action.emoji ?? '•'}
      </span>
      <span className="relative truncate text-[13px] font-semibold leading-tight text-fg">
        {action.label}
      </span>
      <span className="relative truncate text-[11px] leading-tight text-fg-3">
        {agoLabel && lastBy ? (
          <>
            {agoLabel} ·{' '}
            <span style={{ color: lastBy.color }} className="font-semibold">
              {lastBy.name.trim().split(/\s+/)[0]}
            </span>
          </>
        ) : (
          '—'
        )}
      </span>
    </button>
  );
}
